import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Eye,
  // Edit, // Commented out - editing disabled
  X,
  Calendar,
  Calculator
} from 'lucide-react';
import { closingsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { formatDate } from '../../utils/dayjs';
import DataTable from '../ui/DataTable';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';

const ClosingList = () => {
  const navigate = useNavigate();
  const { addNotification } = useAppStore();
  // View state (search/date/pagination) is seeded from the URL so the list is
  // shareable and restored when returning from a closing's detail page.
  const [searchParams] = useSearchParams();
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  // draftSearch / draftDateFilter = what's typed; searchTerm / dateFilter = the
  // applied query. They diverge until the user hits Search, so nothing loads as
  // you type.
  const [draftSearch, setDraftSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [searchTerm, setSearchTerm] = useState(() => readParam(searchParams, 'q', ''));
  const [isSearching, setIsSearching] = useState(() => Boolean(readParam(searchParams, 'q', '')));
  const [searchResults, setSearchResults] = useState([]);
  const [sortBy, setSortBy] = useState(() => readParam(searchParams, 'sort', 'ClosingDate'));
  const [sortOrder, setSortOrder] = useState(() => readParam(searchParams, 'order', 'desc'));
  const [draftDateFilter, setDraftDateFilter] = useState(() => readParam(searchParams, 'date', ''));
  const [dateFilter, setDateFilter] = useState(() => readParam(searchParams, 'date', ''));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => readParam(searchParams, 'page', 1, 'number'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => readParam(searchParams, 'size', 20, 'number'));

  // Ref for search input
  const searchInputRef = useRef(null);

  // Mirror the current view into the URL (defaults omitted). Sort here is a
  // client-side, current-page-only re-order, so it is intentionally not persisted.
  useSyncUrlParams(
    { q: searchTerm.trim(), date: dateFilter, sort: sortBy, order: sortOrder, page: currentPage, size: itemsPerPage },
    { q: '', date: '', sort: 'ClosingDate', order: 'desc', page: 1, size: 20 },
  );
  // Skip the first run of the applied-change / sort effects so restored state
  // isn't clobbered on mount (the initial load below handles it).
  const appliedInitRef = useRef(false);
  const sortInitRef = useRef(false);

  // Focus search input on component mount
  useEffect(() => {
    const focusInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    
    // Try immediately and also with a small delay to ensure DOM is ready
    focusInput();
    const timeoutId = setTimeout(focusInput, 200);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Reload when the APPLIED search / date filter changes (Search / Clear), not as
  // you type. Skips the first run so the mount load below isn't duplicated.
  useEffect(() => {
    if (!appliedInitRef.current) {
      appliedInitRef.current = true;
      return;
    }
    setCurrentPage(1);
    if (searchTerm.trim()) {
      handleRealTimeSearch(1);
    } else {
      setIsSearching(false);
      setSearchResults([]);
      loadData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, dateFilter]);

  // Re-query the current page when the sort changes (server-side sort).
  useEffect(() => {
    if (!sortInitRef.current) {
      sortInitRef.current = true;
      return;
    }
    if (searchTerm.trim()) {
      handleRealTimeSearch(currentPage);
    } else {
      loadData(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  // Column definitions for DataTable
  const columns = [
    {
      key: 'ID',
      title: 'Closing #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Calculator className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">
              Closing #{value}
            </div>
            <div className="text-xs text-gray-500">
              {formatDate(row.ClosingDate)}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'OpeningAmount',
      title: 'Opening',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'CashSales',
      title: 'Cash Sales',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'Refunds',
      title: 'Refunds',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'Expenses',
      title: 'Expenses',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'ClosingAmount',
      title: 'Closing',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'BankReceived',
      title: 'Bank',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'BankRefunds',
      title: 'Bank Refunds',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">
          RS {Number(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'Notes',
      title: 'Notes',
      sortable: false,
      render: (value) => (
        value ? (
          <div className="text-sm text-gray-600 truncate max-w-xs">
            {value}
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleView(row.ID)}
            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
          {/* Edit button commented out - editing disabled */}
          {/* <button
            onClick={() => handleEdit(row.ID)}
            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button> */}
        </div>
      )
    }
  ];

  const loadData = async (page = currentPage, limit = itemsPerPage) => {
    try {
      setLoading(true);
      const skip = (page - 1) * limit;
      const params = { skip, limit, sort: sortBy, order: sortOrder };
      if (dateFilter) {
        params.start_date = new Date(dateFilter).toISOString();
        params.end_date = new Date(dateFilter + "T23:59:59.999").toISOString();
      }

      const response = await closingsAPI.list(params);
      setClosings(response.data);
      
      if (response.data.length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
      setTotalItems((page - 1) * limit + response.data.length);
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        message: 'Failed to fetch closings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeSearch = async (page = 1, limit = itemsPerPage) => {
    if (!searchTerm.trim()) return;
    
    try {
      setIsSearching(true);
      const skip = (page - 1) * limit;
      const params = {
        search: searchTerm,
        skip,
        limit,
        sort: sortBy,
        order: sortOrder
      };
      
      if (dateFilter) {
        params.start_date = new Date(dateFilter).toISOString();
        params.end_date = new Date(dateFilter + "T23:59:59.999").toISOString();
      }

      const response = await closingsAPI.list(params);
      setSearchResults(response.data);
      setClosings(response.data);
      
      // Update pagination for search results
      if (response.data.length === limit) {
        setTotalPages(page + 1);
      } else {
        setTotalPages(page);
      }
      setTotalItems((page - 1) * limit + response.data.length);
    } catch (error) {
      console.error('Error searching closings:', error);
    } finally {
      // Clear the initial page spinner when the first load is a restored search
      // (loadData isn't the one that runs in that case).
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load respects the search/date/page restored from the URL.
    if (searchTerm.trim()) {
      handleRealTimeSearch(currentPage);
    } else {
      loadData(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the typed search + date filter (Search button / Enter). The applied-
  // change effect does the fetch and page reset — nothing loads as you type.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
    setDateFilter(draftDateFilter);
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearchTerm('');
  };

  const clearAll = () => {
    setDraftSearch('');
    setDraftDateFilter('');
    setSearchTerm('');
    setDateFilter('');
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (isSearching) {
      handleRealTimeSearch(page);
    } else {
      loadData(page);
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    if (isSearching) {
      handleRealTimeSearch(1, newItemsPerPage);
    } else {
      loadData(1, newItemsPerPage);
    }
  };

  // Sorting is server-side: set the new sort; the sort effect re-queries.
  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  const handleView = (id) => {
    navigate(`/closings/${id}`);
  };

  // Edit functionality commented out - editing disabled
  // const handleEdit = (id) => {
  //   navigate(`/closings/${id}/edit`);
  // };

  const handleCreateNew = () => {
    navigate('/closings/new');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Daily Closings</h1>
            <p className="text-gray-600">Manage daily shop closing operations and financial reconciliation</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Closing
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        {/* Main Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by ID, notes..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>

        {/* Advanced Filters */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Advanced filters</h3>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear all
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={draftDateFilter}
                  onChange={(e) => setDraftDateFilter(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Indicator */}
      {isSearching && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">
              Showing {closings.length} search results for "{searchTerm}"
            </p>
            <button
              onClick={clearSearch}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Search
            </button>
          </div>
        </div>
      )}

      {/* Closings Table */}
      <DataTable
        columns={columns}
        data={closings}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={closings.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="closings"
      />

    </div>
  );
};

export default ClosingList;
